import { React, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { formatPhoneNumber } from '../../../Utils/phoneUtils';
import { useTheme } from '../../../Context/ThemeContext';
import { useLanguage } from '../../../Context/LanguageContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';

import { ReactComponent as DeleteSvg } from '../../../image/Delete.svg';
import { ReactComponent as LogoGreenSvg } from '../../../image/LogoGreen.svg';
import UserProfile from '../../../Component/Normal/UserProfile';

const CoachSettingInfoBox = (props) => {
  const { data, index, modifyClickFn, deleteClickFn } = props;
  const { isDark } = useTheme();
  const { t, languageMode } = useLanguage();
  const theme = isDark ? darkTheme : lightTheme;

  const [userId, setUserId] = useState();

  const ModifyButtonRef = useRef();
  const DeleteButtonRef = useRef();

  useEffect(() => {
    let userIdList = data.linkCoachId.split('@');

    if (userIdList[0].includes('kakao.')) setUserId(userIdList[0].replaceAll('kakao.', '') + '@' + userIdList[1]);
    else if (userIdList[0].includes('naver.')) setUserId(userIdList[0].replaceAll('naver.', '') + '@' + userIdList[1]);
    else if (userIdList[0].includes('google.')) setUserId(userIdList[0].replaceAll('google.', '') + '@' + userIdList[1]);
    else if (userIdList[0].includes('apple.')) setUserId(userIdList[0].replaceAll('apple.', '') + '@' + userIdList[1]);
    else setUserId(data.linkCoachId);
  }, [])

  function onClickInfoBox(e) {
    if (DeleteButtonRef.current && DeleteButtonRef.current.contains(e.target)) deleteClickFn(index);
    else modifyClickFn(index);
  }

  return (
    <TopLevelWrapper>
      <ImageWrapper>
        <UserProfile profile={data.profile} defaultProfile='https://picture.link-zone.org/ZoneLogo.png' />
      </ImageWrapper>
      <InfoBox $isDark={isDark} onClick={onClickInfoBox}>
        <TopColumnWrapper>
          <DeleteBoxWrapper>
            {
              data.linkCoachId !== 'COACH' &&
              <DeleteBox ref={DeleteButtonRef}>
                <StyledDeleteIcon />
              </DeleteBox>
            }
          </DeleteBoxWrapper>
        </TopColumnWrapper>
        <InnerInfoBox>
          <TitleWrapper>
            <NameWrapper>
              <Name>{data.name}</Name>
            </NameWrapper>
          </TitleWrapper>
          <TotalInfoWrapper>
            <InfoTitleWrapper>
              {
                data.linkCoachId !== 'COACH' &&
                <Column>{t('settingCoach.gender')}</Column>
              }
              <Column style={data.linkCoachId === 'COACH' ? { marginTop: '27px' } : {}}>{t('settingCoach.phone')}</Column>
              <ColumnRowWrapper>
                <LogoWrapper>
                  {
                    data.linkCoachId === 'COACH' ?
                      <LogoImage src='https://picture.link-zone.org/ZoneLogo.png' alt='logo' />
                      :
                      <LogoGreenSvg width={16} height={16} />
                  }
                </LogoWrapper>
                <Column>{data.linkCoachId === 'COACH' ? t('settingCoach.adminId') : t('settingCoach.linkoId')}</Column>
              </ColumnRowWrapper>
            </InfoTitleWrapper>
            <InfoContentWrapper>
              {
                data.linkCoachId !== 'COACH' &&
                <Info>{data.gender === 'Male' ? t('settingCoach.male') : t('settingCoach.female')}</Info>
              }
              <Info style={data.linkCoachId === 'COACH' ? { marginTop: '27px' } : {}}>{formatPhoneNumber(data.phone, data.countryCode, languageMode)}</Info>
              <Info>{userId}</Info>
            </InfoContentWrapper>
          </TotalInfoWrapper>
          <AuthBoxWrapper>
            {
              data.linkCoachId === 'COACH' ?
                <AuthWrapper style={{ width: '100%' }} >
                  <AuthTag backgroundColor={theme.primary} />
                  <AuthBox $isAdmin>
                    <Text style={{ marginTop: '-2px' }} fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.text}>{t('settingCoach.adminAccount')}</Text>
                  </AuthBox>
                </AuthWrapper>
                :
                <AuthTopWrapper>
                  {
                    data.isMemberAuth &&
                    <AuthWrapper>
                      <AuthTag backgroundColor={theme.primary} />
                      <AuthBox>
                        <Text style={{ marginTop: '-2px' }} fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.text}>{t('settingCoach.memberAuth')}</Text>
                      </AuthBox>
                    </AuthWrapper>
                  }
                  {
                    data.isExerciseAuth &&
                    <AuthWrapper>
                      <AuthTag backgroundColor={theme.subKey} />
                      <AuthBox>
                        <Text style={{ marginTop: '-2px' }} fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.text}>{t('settingCoach.exerciseAuth')}</Text>
                      </AuthBox>
                    </AuthWrapper>
                  }
                  {
                    data.isSalesAuth &&
                    <AuthWrapper>
                      <AuthTag backgroundColor={theme.pointYellow} />
                      <AuthBox>
                        <Text style={{ marginTop: '-2px' }} fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.text}>{t('settingCoach.salesAuth')}</Text>
                      </AuthBox>
                    </AuthWrapper>
                  }
                </AuthTopWrapper>
            }
          </AuthBoxWrapper>
        </InnerInfoBox>
      </InfoBox>
    </TopLevelWrapper>
  )
}

export default CoachSettingInfoBox;

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 10px;
`;

const ImageWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 199px;
  height: 199px;

  overflow: hidden;

  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};

  img {
    object-fit: cover;
  }
`;

const ImageView = styled.img`
  width: 199px;
  height: 199px;

  border-radius: 24px;

  object-fit: cover;
`;

const LogoImage = styled.img`
  width: 16px;
  height: 16px;
`;

const InfoBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  box-sizing: border-box;
  width: 298.5px;
  height: 197px;

  border: 1px solid ${props => props.theme.subBackground};
  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};

  transition: all 0.3s ease-in-out;

  @media (hover: hover){
    &:hover {
    cursor: pointer;
    border: 1px solid ${props => props.theme.primary};
    }
  }
`;

const DeleteBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-end;

  margin-top: -1px;
  margin-right: -1px;

  height: 30px;
`;

const DeleteBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 30px;
  height: 30px;

  border-radius: 0px 10px 0px 5px;
  background-color: ${props => props.theme.pointRed}33;

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      background-color: ${props => props.theme.pointRed};
    }
  }
`;

const StyledDeleteIcon = styled(DeleteSvg)`
  width: 20px;
  height: 20px;

  color: ${props => props.theme.white};
`;

const InnerInfoBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  padding: 0 5px 15px 15px;

  box-sizing: border-box;
  width: 100%;
`;

const TopColumnWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  width: 100%;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;

  gap: 4px;
`;

const NameWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  max-width: 100%;
  height: 24px;

  overflow: hidden;
`;

const Name = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 16px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Title = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 16px;

  color: ${props => props.theme.subText};

  margin: 0 0 0 0;

  white-space: nowrap;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 8px;
`;

const Button = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.subText};

  margin: 0 0 0 0;

  transition: all 0.3s ease-in-out;
`;

const TotalInfoWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  margin-top: 5px;

  width: 100%;

  gap: 10px;
`;

const InfoTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  width: 86px;
  flex-shrink: 0;

  gap: 5px;
`;

const InfoContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  flex: 1;
  min-width: 0;

  gap: 5px;

  overflow: hidden;
`;

const Column = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.subText};

  margin: 0 0 0 0;

  height: 21px;
`;

const ColumnRowWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 5px;
`;

const LogoWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 21px;
  height: 21px;

  border-radius: 2px;

  background-color: ${props => props.theme.buttonFill};
`;

const Info = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;

  width: 100%;
  height: 21px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AuthBoxWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;

  margin-top: 25.5px;

  width: 100%;

  gap: 10px;
`;

const AuthTopWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 10px;
`;

const AuthWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;
`;

const AuthTag = styled.div`
  display: flex;
  flex-direction: row;

  width: 8px;
  height: 21px;

  border-radius: 3px 0 0 3px;
  background-color: ${props => props.backgroundColor};
`;

const AuthBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: ${props => props.$isAdmin ? 'calc(100% - 15px)' : '75.5px'};
  height: 21px;

  border-radius: 0 3px 3px 0;
  background-color: ${props => props.theme.buttonFill};
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;
`;
